import {
  CellType,
  ChartSchema,
  ColumnDefinition,
  RowType,
} from '@/features/report-snapshots/api/schema';
import { normalizeCellValue } from '@/features/report-snapshots/utils/common';

export interface ComparisonResult {
  delta?: number;
  hasImproved?: boolean;
  hasIncreased?: boolean;
  unchanged?: boolean;
}

interface SchemaComparator {
  values: (rowIndex: number, colIndex: number) => ComparisonResult;
  summaryRow: (colIndex: number) => ComparisonResult | null;
}

function padValuesToMatchLength(previousValues: RowType[], currentValues: RowType[]): RowType[] {
  const padding = Array(currentValues.length - previousValues.length).fill(null) as RowType[];
  return padding.concat(previousValues);
}

function trimValuesToMatchLength(previousValues: RowType[], currentValues: RowType[]) {
  return previousValues.slice(previousValues.length - currentValues.length);
}

function hasValueImproved(
  currentValue: number,
  previousValue: number,
  improvementDirection: string | undefined,
) {
  if (improvementDirection === 'increasing') {
    return currentValue > previousValue;
  }
  return currentValue < previousValue;
}

function generateComparisonResult(
  currentCellValue: CellType,
  previousCellValue: CellType,
  columnDefinition: ColumnDefinition,
): ComparisonResult {
  const currentValue = normalizeCellValue(currentCellValue, columnDefinition);

  const defaultResult = { unchanged: true };

  if (currentValue === null || isNaN(currentValue)) {
    return defaultResult;
  }

  let previousValue = normalizeCellValue(previousCellValue, columnDefinition);
  if (previousValue === null || isNaN(previousValue)) {
    previousValue = 0;
  }

  if (currentValue === previousValue) {
    return defaultResult;
  }

  const delta = Math.abs(currentValue - previousValue);
  const hasImproved = hasValueImproved(
    currentValue,
    previousValue,
    columnDefinition.improvementDirection,
  );
  const hasIncreased = currentValue > previousValue;

  return { delta, hasImproved, hasIncreased };
}

export function createSchemaComparator(
  currentPeriodSchema: ChartSchema,
  previousPeriodSchema: ChartSchema,
): SchemaComparator {
  const currentValues = currentPeriodSchema.values;
  let previousValues = previousPeriodSchema.values;

  if (currentValues.length > previousValues.length) {
    previousValues = padValuesToMatchLength(previousValues, currentValues);
  } else if (currentValues.length < previousValues.length) {
    previousValues = trimValuesToMatchLength(previousValues, currentValues);
  }

  return {
    values: (rowIndex: number, colIndex: number) => {
      const columnDefinition = currentPeriodSchema.columnDefinitions[colIndex];

      const currentValue = currentValues[rowIndex][colIndex];
      // Explicitly handle case where previousValues array is shorter than currentValues.
      // When arrays have unequal lengths, accessing previousValues[rowIndex] might return undefined.
      const previousValue = previousValues[rowIndex]?.[colIndex];

      const { delta, hasImproved, hasIncreased, unchanged } = generateComparisonResult(
        currentValue,
        previousValue,
        columnDefinition,
      );

      return { delta, hasImproved, hasIncreased, unchanged };
    },
    summaryRow: (colIndex: number) => {
      const columnDefinition = currentPeriodSchema.columnDefinitions[colIndex];
      if (!currentPeriodSchema.summaryRow || !previousPeriodSchema.summaryRow) {
        return null;
      }

      const currentValue = currentPeriodSchema.summaryRow[colIndex];
      const previousValue = previousPeriodSchema.summaryRow[colIndex];

      const { delta, hasImproved, hasIncreased, unchanged } = generateComparisonResult(
        currentValue,
        previousValue,
        columnDefinition,
      );

      return { delta, hasImproved, hasIncreased, unchanged };
    },
  };
}
